import React, { useEffect, useState } from "react";
import Slider from "react-slick";
import { Box, Typography, Button, Card, Skeleton } from "@mui/material";
import { useTheme, useMediaQuery } from "@mui/material";
import { get_amenities } from "../../api/ControllApi";
import { ASSET_BASE } from "../../utils/baseUrls";

const IMAGE_BASE_URL = `${ASSET_BASE}storage/amenities/`;
// CUSTOM ARROWS
function NextArrow({ onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        position: "absolute",
        right: { xs: "-5px", md: "-15px" },
        top: "50%",
        transform: "translateY(-50%)",
        cursor: "pointer",
        zIndex: 20,
      }}
    >
      <img src="/assets/images/right_arrow.png" alt="next" width={35} />
    </Box>
  );
}

function PrevArrow({ onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        position: "absolute",
        left: { xs: "-5px", md: "-15px" },
        top: "50%",
        transform: "translateY(-50%)",
        cursor: "pointer",
        zIndex: 20,
      }}
    >
      <img src="/assets/images/left_arrow.png" alt="prev" width={35} />
    </Box>
  );
}

/** Safely convert any value to an array */
const toArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      return value.split(",").map((s) => s.trim()).filter(Boolean).map((title) => ({ title }));
    }
  }
  if (typeof value === "object") return [value];
  return [];
};

/**
 * Build full image URL.
 * API returns just the filename e.g. "1770896504_swimming pool 1.png"
 */
const getAmenityImageUrl = (image) => {
  if (!image) return "/assets/images/default-icon.png";
  if (image.startsWith("http")) return image;
  return `${IMAGE_BASE_URL}${encodeURIComponent(image)}`;
};

/** Single amenity card — shows image + title */
function AmenityCard({ item }) {
  return (
    <Box sx={{ px: 2, py: 1 }}>
      <Card
        sx={{
          p: 2,
          borderRadius: 3,
          boxShadow: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
          textAlign: "center",
          minHeight: 140,
          justifyContent: "center",
        }}
      >
        <Box
          component="img"
          src={getAmenityImageUrl(item?.image)}
          alt={item?.title || ""}
          onError={(e) => { e.target.src = "/assets/images/default-icon.png"; }}
          sx={{
            width: 60,
            height: 60,
            objectFit: "contain",
            borderRadius: "50%",
            backgroundColor: "#f5f5f5",
            p: 0.75,
            boxShadow: 1,
          }}
        />
        <Typography fontWeight={700} fontSize={14} sx={{ lineHeight: 1.3 }}>
          {item?.title || ""}
        </Typography>
      </Card>
    </Box>
  );
}

/** Skeleton placeholder */
function SkeletonSlide() {
  return (
    <Box sx={{ px: 1 }}>
      <Card sx={{ p: 3, borderRadius: 3, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
        <Skeleton variant="circular" width={60} height={60} />
        <Skeleton variant="text" width={120} height={22} />
      </Card>
    </Box>
  );
}

export default function GiooPropertyLocation({
  address,
  uldNumber,
  amenity,          // array of IDs or objects from parent property data
  commonfacility,
  nearbyLoction,
  loading,
  property_type,
}) {
  const theme  = useTheme();
  const isXs   = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm   = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const [slidesToShow,    setSlidesToShow]    = useState(1);
  const [allAmenities,    setAllAmenities]    = useState([]);
  const [amenityLoading,  setAmenityLoading]  = useState(true);

  // Responsive slide count
  useEffect(() => {
    if (isXs)       setSlidesToShow(1);
    else if (isSm)  setSlidesToShow(2);
    else if (isMdUp) setSlidesToShow(3);
  }, [isXs, isSm, isMdUp]);

  // ✅ Fetch all amenities from the API
  useEffect(() => {
    setAmenityLoading(true);
    get_amenities()
      .then((json) => {
        if (json?.status === 200 && Array.isArray(json?.data?.amenities)) {
          setAllAmenities(json.data.amenities);
        }
      })
      .catch((err) => {})
      .finally(() => setAmenityLoading(false));
  }, []);

  // ✅ Determine which amenities to display
  // `amenity` prop may be: array of IDs (numbers/strings), array of objects {id, title}, or a stringified list
  const propAmenityRaw = toArray(amenity);
  const propAmenityIds = propAmenityRaw
    .map((a) => (typeof a === "object" ? Number(a?.id) : Number(a)))
    .filter(Boolean);

  // If property has specific amenity IDs → filter; otherwise show all
  const displayedAmenities =
    propAmenityIds.length > 0
      ? allAmenities.filter((a) => propAmenityIds.includes(a.id))
      : allAmenities;

  const safeCommonFacility = toArray(commonfacility);
  const nearbyLocations    = toArray(nearbyLoction);

  const sliderBase = {
    dots: false,
    speed: 500,
    slidesToShow,
    slidesToScroll: 1,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
  };

  const sliderFor = (items) => ({
    ...sliderBase,
    infinite: items.length > slidesToShow,
  });

  return (
    <Box
      sx={{
        width: "100%",
        backgroundImage: "url(/assets/images/plootbg.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        py: { xs: 4, md: 6 },
      }}
    >
      {/* HEADER */}
      <Box sx={{ px: { xs: 2, md: 5 }, display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
        <img src="/assets/icons/routing.png" style={{ width: "10%", objectFit: "contain" }} alt="routing" />
        <Box>
          <Typography variant="h6" sx={{ opacity: 0.8, color: "#707070" }} fontWeight={{ xs: 200, md: 300 }}>
            Property Location
          </Typography>
          <Typography variant="h6" fontWeight={{ xs: 200, md: 400 }} sx={{ lineHeight: 1.3, fontSize: { xs: 12, sm: 14, md: 16 } }}>
            {address || ""}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.5 }}>
            <Typography fontWeight={700} color={theme.palette.primary.main}>ULPIN Number:</Typography>
            <Typography fontWeight={700}>{uldNumber || ""}</Typography>
          </Box>
        </Box>
      </Box>

      {/* ── AMENITIES ── */}
      <Box sx={{ px: { xs: 2, md: 5 }, mt: 3 }}>
        <Button sx={{ color: theme.palette.primary.main, fontWeight: 600, fontSize: 16, px: 2, textTransform: "capitalize" }}>
          Amenities Around This Plot Properties
        </Button>
      </Box>

      <Box sx={{ position: "relative", mt: 2, px: { xs: 1, sm: 4 } }}>
        {amenityLoading || loading ? (
          <Slider {...sliderBase}>
            {Array.from({ length: 4 }).map((_, i) => <SkeletonSlide key={i} />)}
          </Slider>
        ) : displayedAmenities.length > 0 ? (
          <Slider {...sliderFor(displayedAmenities)}>
            {displayedAmenities.map((item) => (
              <AmenityCard key={item.id} item={item} />
            ))}
          </Slider>
        ) : (
          <Typography sx={{ px: 2, color: "text.secondary" }}>No Amenities Found</Typography>
        )}
      </Box>

      {/* ── COMMON FACILITIES ── */}
      <Box sx={{ px: { xs: 2, md: 5 }, mt: 4 }}>
        <Button sx={{ color: theme.palette.primary.main, fontWeight: 600, fontSize: 16, px: 2, textTransform: "capitalize" }}>
          Common Facilities
        </Button>
      </Box>

      <Box sx={{ position: "relative", mt: 2, px: { xs: 1, sm: 4 } }}>
        {loading ? (
          <Slider {...sliderBase}>
            {Array.from({ length: 4 }).map((_, i) => <SkeletonSlide key={i} />)}
          </Slider>
        ) : safeCommonFacility.length > 0 ? (
          <Slider {...sliderFor(safeCommonFacility)}>
            {safeCommonFacility.map((item, index) => (
              <Box key={index} sx={{ px: 2, py: 1 }}>
                <Card sx={{ p: 2, borderRadius: 3, boxShadow: 4, display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, textAlign: "center", minHeight: 140, justifyContent: "center" }}>
                  <Box
                    component="img"
                    src={getAmenityImageUrl(item?.image)}
                    alt={item?.title || ""}
                    onError={(e) => { e.target.src = "/assets/images/default-icon.png"; }}
                    sx={{ width: 60, height: 60, objectFit: "contain", borderRadius: "50%", backgroundColor: "#f5f5f5", p: 0.75, boxShadow: 1 }}
                  />
                  <Typography fontWeight={700} fontSize={14}>{item?.title || item || ""}</Typography>
                </Card>
              </Box>
            ))}
          </Slider>
        ) : (
          <Typography sx={{ px: 2, color: "text.secondary" }}>No Common Facilities Found</Typography>
        )}
      </Box>

      {/* ── NEARBY LOCATIONS ── */}
      <Box sx={{ px: { xs: 2, md: 5 }, mt: 4 }}>
        <Button sx={{ color: theme.palette.primary.main, fontWeight: 600, fontSize: 16, px: 2, textTransform: "capitalize" }}>
          Nearby Locations
        </Button>
      </Box>

      <Box sx={{ position: "relative", mt: 2, px: { xs: 1, sm: 4 } }}>
        {loading ? (
          <Slider {...sliderBase}>
            {Array.from({ length: 4 }).map((_, i) => <SkeletonSlide key={i} />)}
          </Slider>
        ) : nearbyLocations.length > 0 ? (
          <Slider {...sliderFor(nearbyLocations)}>
            {nearbyLocations.map((item, index) => (
              <Box key={index} sx={{ px: 1 }}>
                <Card sx={{ p: 3, borderRadius: 3, mb: 1, boxShadow: 4, display: "flex", flexDirection: "column", gap: 2, minWidth: 180 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      component="img"
                      src={item?.image ? (item.image.startsWith("http") ? item.image : `${ASSET_BASE}storage/${item.image}`) : "/assets/images/placeholder_img.png"}
                      alt={item?.title || ""}
                      onError={(e) => { e.target.src = "/assets/images/placeholder_img.png"; }}
                      sx={{ width: 55, height: 55, objectFit: "contain", borderRadius: "50%", backgroundColor: "#f5f5f5", p: 0.5, boxShadow: 1 }}
                    />
                    <Box>
                      <Typography fontWeight={700} fontSize={17} sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {item?.title || ""}
                      </Typography>
                      <Typography fontSize={14} color="text.secondary">
                        {item?.pivot?.distance || item?.distance || ""}
                        {(item?.pivot?.distance || item?.distance) ? " Km" : ""}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Box>
            ))}
          </Slider>
        ) : (
          <Typography sx={{ px: 2, color: "text.secondary" }}>No Nearby Locations Found</Typography>
        )}
      </Box>
    </Box>
  );
}